import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { getAdminAnalytics } from "@/lib/admin.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { getCtaAttribution } from "@/lib/cta.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  head: () => ({ meta: [{ title: "Admin Analytics — The Hospitality Reserve" }] }),
  component: AdminAnalytics,
});

type Analytics = Awaited<ReturnType<typeof getAdminAnalytics>>;

const gbp = (pence: number) => `£${(pence / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function AdminAnalytics() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(getAdminAnalytics);
  const loadCta = useServerFn(getCtaAttribution);
  const [data, setData] = useState<Analytics | null>(null);
  const [cta, setCta] = useState<Awaited<ReturnType<typeof getCtaAttribution>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      try {
        const [main, ctaRes] = await Promise.all([load(), loadCta().catch(() => null)]);
        setData(main);
        setCta(ctaRes);
      }
      catch (e) { toast.error(e instanceof Error ? e.message : "Failed to load analytics."); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin Analytics</p>
            <h1 className="mt-3 font-display text-3xl">Platform Overview</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Console</Link>
            <Link to="/admin/disputes" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Disputes</Link>
          </div>
        </div>
        <span className="gold-divider mt-4" />

        {loading || !data ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <section className="mt-8">
              <h2 className="font-display text-xl">Revenue</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Platform earnings · all time" value={gbp(data.revenue.feesAllTimePence)} hint="Flat £5 per booked bartender" />
                <Stat label="Platform earnings · 30d" value={gbp(data.revenue.fees30Pence)} />
                <Stat label="Platform earnings · 90d" value={gbp(data.revenue.fees90Pence)} />
                <Stat label="GMV · 30 days" value={gbp(data.revenue.gmv30Pence)} />
                <Stat label="GMV · 90 days" value={gbp(data.revenue.gmv90Pence)} />
                <Stat label="Payouts pending" value={gbp(data.revenue.payoutPendingPence)} />
                <Stat label="Payouts paid out" value={gbp(data.revenue.payoutPaidPence)} />
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-display text-xl">Marketplace Health</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Shifts (90d)" value={String(data.marketplace.totalShifts90)} />
                <Stat label="Open" value={String(data.marketplace.openShifts)} />
                <Stat label="Filled" value={String(data.marketplace.filledShifts)} />
                <Stat label="Cancelled" value={String(data.marketplace.cancelledShifts)} />
                <Stat label="Fill rate" value={`${data.marketplace.fillRate}%`} />
                <Stat label="Cancel rate" value={`${data.marketplace.cancelRate}%`} />
                <Stat label="Avg rating" value={data.avgRating != null ? `${data.avgRating} / 5` : "—"} hint={`${data.reviewCount} reviews`} />
                <Stat label="Open disputes" value={String(data.openDisputeCount)} />
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-display text-xl">User Growth</h2>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Bartenders total" value={String(data.users.bartendersTotal)} />
                <Stat label="Venues total" value={String(data.users.venuesTotal)} />
                <Stat label="New bartenders · 7d" value={String(data.users.newBartenders7)} />
                <Stat label="New venues · 7d" value={String(data.users.newVenues7)} />
                <Stat label="Pending approvals" value={String(data.users.pendingApprovals)} />
              </div>
            </section>

            <section className="mt-10 grid md:grid-cols-2 gap-6">
              <TopList title="Top venues · 90d" items={data.topVenues.map((v) => ({ id: v.id, name: v.name, meta: `${v.shifts} paid shifts` }))} />
              <TopList title="Top bartenders · all time" items={data.topBartenders.map((b) => ({ id: b.id, name: b.name, meta: `${b.completed} completed` }))} />
            </section>

            <CtaAttributionSection cta={cta} />
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-border bg-card p-5">
      <p className="text-xs tracking-wide-luxury text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-2xl">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function TopList({ title, items }: { title: string; items: Array<{ id: string; name: string; meta: string }> }) {
  return (
    <div className="border border-border bg-card p-5">
      <p className="text-xs tracking-wide-luxury text-muted-foreground">{title}</p>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">No data yet.</p>
      ) : (
        <ol className="mt-3 space-y-2">
          {items.map((i, idx) => (
            <li key={i.id} className="flex justify-between text-sm border-t border-border pt-2 first:border-0 first:pt-0">
              <span><span className="text-muted-foreground mr-2">{idx + 1}.</span>{i.name}</span>
              <span className="text-accent">{i.meta}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

type CtaData = Awaited<ReturnType<typeof getCtaAttribution>> | null;

function CtaAttributionSection({ cta }: { cta: CtaData }) {
  const [campaignFilter, setCampaignFilter] = useState("");
  const [shiftFilter, setShiftFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [destFilter, setDestFilter] = useState("");

  const summary = cta?.summary ?? [];
  const recent = cta?.recent ?? [];
  const tokens = cta?.tokens ?? [];

  const filtered = useMemo(() => {
    return summary.filter((r) =>
      (!campaignFilter || (r.campaign ?? "").toLowerCase().includes(campaignFilter.toLowerCase())) &&
      (!shiftFilter || (r.shift_id ?? "").includes(shiftFilter)) &&
      (!statusFilter || (r.onboarding_status ?? "") === statusFilter) &&
      (!destFilter || (r.destination ?? "").includes(destFilter)),
    );
  }, [summary, campaignFilter, shiftFilter, statusFilter, destFilter]);

  const totals = useMemo(() => {
    const events = filtered.reduce((s, r) => s + (r.event_count ?? 0), 0);
    const users = filtered.reduce((s, r) => s + (r.user_count ?? 0), 0);
    const consumed = tokens.filter((t) => t.consumed_at).length;
    return { events, users, consumed, tokens: tokens.length };
  }, [filtered, tokens]);

  const statuses = useMemo(
    () => Array.from(new Set(summary.map((r) => r.onboarding_status).filter(Boolean))) as string[],
    [summary],
  );

  if (!cta) {
    return (
      <section className="mt-10">
        <h2 className="font-display text-xl">CTA Attribution</h2>
        <p className="mt-2 text-sm text-muted-foreground">No CTA data available yet.</p>
      </section>
    );
  }

  return (
    <section className="mt-10">
      <h2 className="font-display text-xl">CTA Attribution</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Email CTA redirects grouped by campaign, shift, and final destination.
      </p>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Filtered events" value={String(totals.events)} />
        <Stat label="Distinct users" value={String(totals.users)} />
        <Stat label="Tokens issued (recent)" value={String(totals.tokens)} />
        <Stat label="Tokens consumed" value={String(totals.consumed)} />
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
        <input value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} placeholder="Campaign…" className="border border-border bg-card px-3 py-2 text-sm" />
        <input value={shiftFilter} onChange={(e) => setShiftFilter(e.target.value)} placeholder="Shift id contains…" className="border border-border bg-card px-3 py-2 text-sm" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-border bg-card px-3 py-2 text-sm">
          <option value="">All onboarding statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={destFilter} onChange={(e) => setDestFilter(e.target.value)} placeholder="Destination contains…" className="border border-border bg-card px-3 py-2 text-sm" />
      </div>

      <div className="mt-4 overflow-x-auto border border-border bg-card">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Campaign</th>
              <th className="px-3 py-2 text-left">Shift</th>
              <th className="px-3 py-2 text-left">Stage</th>
              <th className="px-3 py-2 text-left">Destination</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Onboarding</th>
              <th className="px-3 py-2 text-right">Events</th>
              <th className="px-3 py-2 text-right">Users</th>
              <th className="px-3 py-2 text-left">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-muted-foreground">No matching events.</td></tr>
            ) : filtered.slice(0, 100).map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2">{r.campaign ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{r.shift_id ? r.shift_id.slice(0, 8) : "—"}</td>
                <td className="px-3 py-2">{r.stage ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{r.destination ?? "—"}</td>
                <td className="px-3 py-2">{r.role ?? "—"}</td>
                <td className="px-3 py-2">{r.onboarding_status ?? "—"}</td>
                <td className="px-3 py-2 text-right">{r.event_count ?? 0}</td>
                <td className="px-3 py-2 text-right">{r.user_count ?? 0}</td>
                <td className="px-3 py-2">{r.last_seen_at ? new Date(r.last_seen_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="mt-8 font-display text-base">Recent CTA events</h3>
      <div className="mt-2 overflow-x-auto border border-border bg-card">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Time</th>
              <th className="px-3 py-2 text-left">Stage</th>
              <th className="px-3 py-2 text-left">Campaign</th>
              <th className="px-3 py-2 text-left">Destination</th>
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">User</th>
            </tr>
          </thead>
          <tbody>
            {recent.slice(0, 30).map((r, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2">{r.stage ?? "—"}</td>
                <td className="px-3 py-2">{r.campaign ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{r.to_path ?? "—"}</td>
                <td className="px-3 py-2">{r.reason ?? "—"}</td>
                <td className="px-3 py-2 font-mono">{r.user_id ? r.user_id.slice(0, 8) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
